import {z} from "zod/v4-mini"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {PointGeometryLinePoints1} from "#template/nodes/geometry-graph/attribute/primitive/point-geometry-line-points-1"
import {PointGeometryLinePoints2} from "#template/nodes/geometry-graph/attribute/primitive/point-geometry-line-points-2"
import {PointGeometryLinePoints3} from "#template/nodes/geometry-graph/attribute/primitive/point-geometry-line-points-3"
import {PointGeometryPointSet1} from "#template/nodes/geometry-graph/attribute/primitive/point-geometry-point-set-1"
import {PointGeometryPointSet2} from "#template/nodes/geometry-graph/attribute/primitive/point-geometry-point-set-2"
import {PointGeometryPointSet3} from "#template/nodes/geometry-graph/attribute/primitive/point-geometry-point-set-3"
import {CurveGeometryLine1} from "#template/nodes/geometry-graph/attribute/primitive/curve-geometry-line-1"
import {CurveGeometryLine2} from "#template/nodes/geometry-graph/attribute/primitive/curve-geometry-line-2"
import {CurveGeometryLine3} from "#template/nodes/geometry-graph/attribute/primitive/curve-geometry-line-3"
import {MeshGeometryGrid2} from "#template/nodes/geometry-graph/attribute/primitive/mesh-geometry-grid-2"
import {
    pointGeometryPrimitiveOptions,
    curveGeometryPrimitiveOptions,
    meshGeometryPrimitiveOptions,
    type PointGeometryPrimitiveOption,
    type CurveGeometryPrimitiveOption,
    type MeshGeometryPrimitiveOption,
} from "#template/nodes/geometry-graph/options"

export const pointGeometryPrimitiveOption: z.ZodMiniType<PointGeometryPrimitiveOption, PointGeometryPrimitiveOption> = z.enum(pointGeometryPrimitiveOptions)
export const curveGeometryPrimitiveOption: z.ZodMiniType<CurveGeometryPrimitiveOption, CurveGeometryPrimitiveOption> = z.enum(curveGeometryPrimitiveOptions)
export const meshGeometryPrimitiveOption: z.ZodMiniType<MeshGeometryPrimitiveOption, MeshGeometryPrimitiveOption> = z.enum(meshGeometryPrimitiveOptions)

export type PointGeometryPrimitiveLinePointsLike = PointGeometryLinePoints1 | PointGeometryLinePoints2 | PointGeometryLinePoints3
export type PointGeometryPrimitivePointSetLike = PointGeometryPointSet1 | PointGeometryPointSet2 | PointGeometryPointSet3
export type PointGeometryPrimitiveLike = PointGeometryPrimitiveLinePointsLike | PointGeometryPrimitivePointSetLike
export type CurveGeometryPrimitiveLike = CurveGeometryLine1 | CurveGeometryLine2 | CurveGeometryLine3
export type MeshGeometryPrimitiveLike = MeshGeometryGrid2

export const isPointGeometryPrimitiveLinePointsLike = (x: unknown): x is PointGeometryPrimitiveLinePointsLike =>
    x instanceof PointGeometryLinePoints1 || x instanceof PointGeometryLinePoints2 || x instanceof PointGeometryLinePoints3

export const isPointGeometryPrimitivePointSetLike = (x: unknown): x is PointGeometryPrimitivePointSetLike =>
    x instanceof PointGeometryPointSet1 || x instanceof PointGeometryPointSet2 || x instanceof PointGeometryPointSet3

export const isPointGeometryPrimitiveLike = (x: unknown): x is PointGeometryPrimitiveLike =>
    isPointGeometryPrimitiveLinePointsLike(x) || isPointGeometryPrimitivePointSetLike(x)

export const isCurveGeometryPrimitiveLike = (x: unknown): x is CurveGeometryPrimitiveLike =>
    x instanceof CurveGeometryLine1 || x instanceof CurveGeometryLine2 || x instanceof CurveGeometryLine3

export const isMeshGeometryPrimitiveLike = (x: unknown): x is MeshGeometryPrimitiveLike => x instanceof MeshGeometryGrid2

export function createPointGeometryPrimitive(
    option: PointGeometryPrimitiveOption,
    args:
        | {
              numPoints: ScalarArg | null
              x0: ScalarArg | null
              x1: ScalarArg | null
              y0?: ScalarArg | null
              y1?: ScalarArg | null
              z0?: ScalarArg | null
              z1?: ScalarArg | null
          }
        | {
              data: ScalarArg[] | {x: ScalarArg | null; y: ScalarArg | null}[] | {x: ScalarArg | null; y: ScalarArg | null; z: ScalarArg | null}[]
          },
): PointGeometryPrimitiveLike {
    const ensureArg = (arg: ScalarArg | null | undefined) => {
        if (arg === undefined) throw new Error("Invalid argument in createPointGeometryPrimitive")
        return arg
    }

    if (option.startsWith("linePoints")) {
        const parsedArgs = z
            .object({
                numPoints: z.nullable(scalarArg),
                x0: z.nullable(scalarArg),
                x1: z.nullable(scalarArg),
                y0: z.optional(z.nullable(scalarArg)),
                y1: z.optional(z.nullable(scalarArg)),
                z0: z.optional(z.nullable(scalarArg)),
                z1: z.optional(z.nullable(scalarArg)),
            })
            .safeParse(args)

        if (!parsedArgs.success) throw new Error(`Invalid arguments for createPointGeometryPrimitive: ${parsedArgs.error.message}`)

        const {x0, x1, y0, y1, z0, z1, numPoints} = parsedArgs.data

        if (option === "linePoints1") return new PointGeometryLinePoints1({parameters: {x0, x1, numPoints}})
        else if (option === "linePoints2") return new PointGeometryLinePoints2({parameters: {x0, y0: ensureArg(y0), x1, y1: ensureArg(y1), numPoints}})
        else if (option === "linePoints3") {
            return new PointGeometryLinePoints3({
                parameters: {x0, y0: ensureArg(y0), z0: ensureArg(z0), x1, y1: ensureArg(y1), z1: ensureArg(z1), numPoints},
            })
        } else {
            throw new Error(`Unknown point geometry primitive option: ${option}`)
        }
    } else if (option.startsWith("pointSet")) {
        const parsedArgs = z
            .object({
                data: z.union([
                    z.array(z.nullable(scalarArg)),
                    z.array(z.object({x: z.nullable(scalarArg), y: z.nullable(scalarArg), z: z.nullable(scalarArg)})),
                    z.array(z.object({x: z.nullable(scalarArg), y: z.nullable(scalarArg)})),
                ]),
            })
            .safeParse(args)

        if (!parsedArgs.success) throw new Error(`Invalid arguments for createPointGeometryPrimitive: ${parsedArgs.error.message}`)

        const {data} = parsedArgs.data

        if (option === "pointSet1") {
            return new PointGeometryPointSet1({parameters: {data: data as (ScalarArg | null)[]}})
        } else if (option === "pointSet2") {
            return new PointGeometryPointSet2({parameters: {data: data as {x: ScalarArg | null; y: ScalarArg | null}[]}})
        } else if (option === "pointSet3") {
            return new PointGeometryPointSet3({parameters: {data: data as {x: ScalarArg | null; y: ScalarArg | null; z: ScalarArg | null}[]}})
        } else {
            throw new Error(`Unknown point geometry primitive option: ${option}`)
        }
    }

    throw new Error(`Unknown point geometry primitive option: ${option}`)
}

export function createCurveGeometryPrimitive(
    option: CurveGeometryPrimitiveOption,
    numSegments: ScalarArg | null,
    args: {
        x0: ScalarArg | null
        x1: ScalarArg | null
        y0?: ScalarArg | null
        y1?: ScalarArg | null
        z0?: ScalarArg | null
        z1?: ScalarArg | null
    },
): CurveGeometryPrimitiveLike {
    const ensureArg = (arg: ScalarArg | null | undefined) => {
        if (arg === undefined) throw new Error("Invalid argument in createCurveGeometryPrimitive")
        return arg
    }
    const parsedArgs = z
        .object({
            x0: z.nullable(scalarArg),
            x1: z.nullable(scalarArg),
            y0: z.optional(z.nullable(scalarArg)),
            y1: z.optional(z.nullable(scalarArg)),
            z0: z.optional(z.nullable(scalarArg)),
            z1: z.optional(z.nullable(scalarArg)),
        })
        .safeParse(args)

    if (!parsedArgs.success) throw new Error(`Invalid arguments for createCurveGeometryPrimitive: ${parsedArgs.error.message}`)

    const {x0, x1, y0, y1, z0, z1} = parsedArgs.data

    if (option === "line1") return new CurveGeometryLine1({parameters: {x0, x1, numSegments}})
    else if (option === "line2") return new CurveGeometryLine2({parameters: {x0, y0: ensureArg(y0), x1, y1: ensureArg(y1), numSegments}})
    else if (option === "line3") {
        return new CurveGeometryLine3({
            parameters: {x0, y0: ensureArg(y0), z0: ensureArg(z0), x1, y1: ensureArg(y1), z1: ensureArg(z1), numSegments},
        })
    } else {
        throw new Error(`Unknown curve geometry primitive option: ${option}`)
    }
}

export function createMeshGeometryPrimitive(
    option: MeshGeometryPrimitiveOption,
    args: {
        originX: ScalarArg | null
        originY: ScalarArg | null
        basis1X: ScalarArg | null
        basis1Y: ScalarArg | null
        basis2X: ScalarArg | null
        basis2Y: ScalarArg | null
        numXSegments: ScalarArg | null
        numYSegments: ScalarArg | null
    },
): MeshGeometryPrimitiveLike {
    const parsedArgs = z
        .object({
            originX: z.nullable(scalarArg),
            originY: z.nullable(scalarArg),
            basis1X: z.nullable(scalarArg),
            basis1Y: z.nullable(scalarArg),
            basis2X: z.nullable(scalarArg),
            basis2Y: z.nullable(scalarArg),
            numXSegments: z.nullable(scalarArg),
            numYSegments: z.nullable(scalarArg),
        })
        .safeParse(args)

    if (!parsedArgs.success) throw new Error(`Invalid arguments for createMeshGeometryPrimitive: ${parsedArgs.error.message}`)

    const {originX, originY, basis1X, basis1Y, basis2X, basis2Y, numXSegments, numYSegments} = parsedArgs.data

    if (option === "grid2") {
        return new MeshGeometryGrid2({
            parameters: {originX, originY, basis1X, basis1Y, basis2X, basis2Y, numXSegments, numYSegments},
        })
    } else {
        throw new Error(`Unknown mesh geometry primitive option: ${option}`)
    }
}

export function optionForPointGeometryPrimitive(primitive: PointGeometryPrimitiveLike): PointGeometryPrimitiveOption {
    if (primitive instanceof PointGeometryPointSet1) return "pointSet1"
    else if (primitive instanceof PointGeometryPointSet2) return "pointSet2"
    else if (primitive instanceof PointGeometryPointSet3) return "pointSet3"
    else if (primitive instanceof PointGeometryLinePoints1) return "linePoints1"
    else if (primitive instanceof PointGeometryLinePoints2) return "linePoints2"
    else if (primitive instanceof PointGeometryLinePoints3) return "linePoints3"
    else throw new Error(`Unknown point geometry primitive: ${(primitive as {nodeClass: string}).nodeClass}`)
}

export function optionForCurveGeometryPrimitive(primitive: CurveGeometryPrimitiveLike): CurveGeometryPrimitiveOption {
    if (primitive instanceof CurveGeometryLine1) return "line1"
    else if (primitive instanceof CurveGeometryLine2) return "line2"
    else if (primitive instanceof CurveGeometryLine3) return "line3"
    else throw new Error(`Unknown curve geometry primitive: ${(primitive as {nodeClass: string}).nodeClass}`)
}

export function optionForMeshGeometryPrimitive(primitive: MeshGeometryPrimitiveLike): MeshGeometryPrimitiveOption {
    if (primitive instanceof MeshGeometryGrid2) return "grid2"
    else throw new Error(`Unknown mesh geometry primitive: ${(primitive as {nodeClass: string}).nodeClass}`)
}
